import {
  cloneDeep,
  filter,
  find,
  findIndex,
  forOwn,
  get,
  has,
  isEqual,
} from 'lodash';

import {
  KEYWORD_PROP_CONTENT_VALUE,
  KEYWORD_PROP_CONTENT_VALIDATED,
} from 'src/js/model/auto_layout_screen_data_helpers';

const IGNORED_KEYS = [KEYWORD_PROP_CONTENT_VALIDATED];

class DirtyCheckingService {
  static setSavedOrDeletedFlag(ctrl) {
    /* eslint-disable no-param-reassign */
    // inform child controls about saving
    ctrl.savedOrDeleted = true;
    // reset savedOrDeleted switch in next digest cycle
    setTimeout(() => {
      ctrl.savedOrDeleted = false;
    });
    /* eslint-enable no-param-reassign */
  }

  static isDataChangeDirty(dlOnDataChangeData, originalData) {
    let oldData;
    let newData;
    let isDirty = false;

    if (dlOnDataChangeData) {
      // get original data before change, it can be send in callback,
      // or it is the part of data that was changed from loadedData in this component
      oldData =
        dlOnDataChangeData.oldData ||
        get(originalData, dlOnDataChangeData.pathToData) ||
        get(originalData, dlOnDataChangeData.id);

      oldData =
        oldData && has(oldData, KEYWORD_PROP_CONTENT_VALUE)
          ? oldData[KEYWORD_PROP_CONTENT_VALUE]
          : oldData;
      newData =
        dlOnDataChangeData.data &&
        has(dlOnDataChangeData.data, KEYWORD_PROP_CONTENT_VALUE)
          ? dlOnDataChangeData.data[KEYWORD_PROP_CONTENT_VALUE]
          : dlOnDataChangeData.data;
      // compare loaded data with data of change and set appropriate dirty value
      // TODO: detect reverse change more precisely (problem of non-reversible null value)
      isDirty =
        !isEqual(newData, oldData) && !(oldData === null && newData === '');
    }
    return isDirty;
  }

  static _ignoredKeysFilter(key) {
    return key && IGNORED_KEYS.indexOf(key) !== -1;
  }

  constructor() {
    this.dirtyChanges = [];
  }

  registerDirtyChange(contextParams, screenId, dirtyValue, dlOnDataChangeData) {
    let itemIdx;
    const itemId = dlOnDataChangeData
      ? dlOnDataChangeData.pathToData || dlOnDataChangeData.id
      : null;
    const dirtyItem = {contextParams, screenId};
    if (itemId) {
      dirtyItem.itemId = itemId;
    }
    itemIdx = findIndex(this.dirtyChanges, dirtyItem);
    if (dirtyValue) {
      if (itemIdx === -1) {
        this.dirtyChanges.push(dirtyItem);
      }
    } else {
      // remove all occurrences of screen change
      while (itemIdx >= 0) {
        this.dirtyChanges.splice(itemIdx, 1);
        itemIdx = findIndex(this.dirtyChanges, dirtyItem);
      }
    }
  }

  isScreenDirty(contextParams, screenId) {
    return find(this.dirtyChanges, {contextParams, screenId}) !== undefined;
  }

  isScreenGroupDirty(contextParams, screenId) {
    const documentDirtyScreens = filter(this.dirtyChanges, {contextParams});
    return (
      documentDirtyScreens !== undefined &&
      find(
        documentDirtyScreens,
        (item) =>
          item.screenId === screenId ||
          item.screenId.substring(0, item.screenId.lastIndexOf('.')) ===
            screenId.substring(0, screenId.lastIndexOf('.'))
      ) !== undefined
    );
  }

  isDocumentDirty(contextParams) {
    return find(this.dirtyChanges, {contextParams}) !== undefined;
  }

  _omitByDeep(obj, predicate) {
    const newObj = cloneDeep(obj);

    forOwn(newObj, (propName) => {
      if (typeof newObj[propName] === 'object') {
        newObj[propName] = this._omitByDeep(newObj[propName], predicate);
      } else if (predicate(propName)) {
        delete newObj[propName];
      }
    });
    return newObj;
  }

  isDataEqual(dataA, dataB) {
    return isEqual(
      this._omitByDeep(dataA, this._ignoredKeysFilter.bind(this)),
      this._omitByDeep(dataB, this._ignoredKeysFilter.bind(this))
    );
  }
}

export default DirtyCheckingService;
